import { inject, Injectable } from '@angular/core';
import {
  ApiDataAccess,
  HttpApiQueryRepository,
  IsoDate,
  Query,
  QueryBuilder,
  QueryFilter,
} from '@softline/core';

/** Backend exposes a single uniform `statistics.*` UNION prefix per
    DocumentType/StatisticType combination — aggregate columns (`net`,
    `cost`, `contribution_margin`, `discount_value`), the date filter
    (`document_date`), and the response buckets all use this prefix.
    Mirrors `STATISTICS_PREFIX` from the sales-statistics module helpers. */
const STATISTICS_PREFIX = 'statistics';

const SALES_STATISTICS_PER_TYPE =
  '/statistics-service/statistics/sales-statistics/{documentType}/{statisticType}';

interface ApiRow {
  [key: string]: unknown;
}

interface LoadResult {
  data: ApiRow[];
}

export interface CustomerStatisticsTotals {
  netto: number;
  cost: number;
  db: number;
  dbPercent: number;
}

export interface CustomerStatisticsSeriesRow {
  /** Month bucket as `YYYY-MM` (sorted ascending). */
  field: string;
  netto: number;
  cost: number;
  db: number;
  dbPercent: number;
}

/** Both periods of the customer Umsatz comparison, fetched in ONE request. */
export interface CustomerStatisticsComparison {
  /** Current-period monthly series (sorted ascending). */
  series: CustomerStatisticsSeriesRow[];
  /** Current-period totals. */
  current: CustomerStatisticsTotals;
  /** Comparison-period totals (prior year). */
  previous: CustomerStatisticsTotals;
}

class StatisticsRepo extends HttpApiQueryRepository<object> {
  constructor(dataAccess: ApiDataAccess, documentType: string, statisticType: string) {
    super(
      dataAccess,
      SALES_STATISTICS_PER_TYPE.replace('{documentType}', documentType).replace(
        '{statisticType}',
        statisticType
      )
    );
  }
}

function addValueColumns(builder: QueryBuilder<ApiRow>): void {
  builder.column(`${STATISTICS_PREFIX}.net`, { usage: 'aggregate', agg: 'sum' });
  builder.column(`${STATISTICS_PREFIX}.cost`, { usage: 'aggregate', agg: 'sum' });
  builder.column(`${STATISTICS_PREFIX}.contribution_margin`, {
    usage: 'aggregate',
    agg: 'sum',
  });
  builder.column(`${STATISTICS_PREFIX}.discount_value`, { usage: 'aggregate', agg: 'sum' });
}

function addDateFilter(builder: QueryBuilder<ApiRow>, from: string, to: string): void {
  builder.filter({
    type: 'dateInRange',
    name: `${STATISTICS_PREFIX}.document_date`,
    valueFrom: from as IsoDate,
    valueTo: to as IsoDate,
  });
}

/** Group by `query_period` and OR-filter both date ranges so the backend
 *  returns the current and previous periods in ONE request, each row tagged
 *  with `query_period = 'current' | 'previous'`. Mirrors the sales-statistics
 *  module's single-request comparison (addPeriodColumns / addPeriodFilters). */
function addPeriodColumns(builder: QueryBuilder<ApiRow>): void {
  builder.column('query_period', { usage: 'group' });
}

function addPeriodFilters(
  builder: QueryBuilder<ApiRow>,
  primary: { from: string; to: string },
  reference: { from: string; to: string }
): void {
  builder.filter({
    type: 'conditions',
    operator: 'OR',
    filters: [
      {
        type: 'dateInRange',
        name: `${STATISTICS_PREFIX}.document_date`,
        valueFrom: primary.from as IsoDate,
        valueTo: primary.to as IsoDate,
      },
      {
        type: 'dateInRange',
        name: `${STATISTICS_PREFIX}.document_date`,
        valueFrom: reference.from as IsoDate,
        valueTo: reference.to as IsoDate,
      },
    ],
  } as unknown as QueryFilter<ApiRow>);
}

function periodOf(row: ApiRow): 'current' | 'previous' | null {
  const p = row['query_period'];
  return p === 'current' || p === 'previous' ? p : null;
}

/** Sum the value buckets across a set of period rows into one totals object. */
function sumTotals(rows: ApiRow[]): CustomerStatisticsTotals {
  let netto = 0;
  let cost = 0;
  let db = 0;
  for (const row of rows) {
    netto += val(row, 'net');
    cost += val(row, 'cost');
    db += val(row, 'contribution_margin');
  }
  const dbPercent = netto !== 0 ? (db / netto) * 100 : 0;
  return { netto, cost, db, dbPercent };
}

function addCustomerFilter(
  builder: QueryBuilder<ApiRow>,
  customerNumber: number | string,
  subNumber: number | string = 0
): void {
  // Mirror the current sales-statistics module's customer filter exactly: an
  // `OR` of one `AND(customer.number, customer.sub_number)` group (see
  // SalesStatisticsQueryService.activeFilterEntries). The live endpoint matches
  // on this composite shape; a pair of flat top-level `stringEquals` filters
  // returned no rows (KPIs showed €0,00 / -100%).
  builder.filter({
    type: 'conditions',
    operator: 'OR',
    filters: [
      {
        type: 'conditions',
        operator: 'AND',
        filters: [
          { type: 'stringEquals', name: 'customer.number', value: String(customerNumber) },
          { type: 'stringEquals', name: 'customer.sub_number', value: String(subNumber) },
        ],
      },
    ],
  } as unknown as QueryFilter<ApiRow>);
}

function val(row: ApiRow, field: string): number {
  const bucket = row[STATISTICS_PREFIX] as Record<string, unknown> | undefined;
  const v = bucket?.[`${field}:sum`] ?? bucket?.[field];
  return (typeof v === 'number' ? v : 0) ?? 0;
}

/** Lightweight stateless wrapper around the sales-statistics endpoint —
 *  scoped to a single customer + date range for the Umsatz tab of the
 *  customer overview. Unlike `TotalChartService` / `NettoChartService`
 *  in the sales-statistics module, this one doesn't depend on the
 *  global `SalesStatisticsQueryService` so it can be called imperatively
 *  with plain inputs. */
@Injectable()
export class CustomerStatisticsService {
  private readonly dataAccess = inject(ApiDataAccess);

  /**
   * Loads the current period (monthly series + totals) AND the comparison
   * period (totals) in a SINGLE request, mirroring the sales-statistics
   * module's `query_period` split. Each row is tagged `query_period =
   * 'current' | 'previous'`; rows are bucketed by that tag.
   */
  async loadComparison(
    customerNumber: number | string,
    subNumber: number | string,
    primary: { from: string; to: string },
    reference: { from: string; to: string },
    documentType = 'INVOICE',
    statisticType = 'TOTAL'
  ): Promise<CustomerStatisticsComparison> {
    const repo = new StatisticsRepo(this.dataAccess, documentType, statisticType);
    const builder = new QueryBuilder<ApiRow>()
      .start(0)
      .end(10000)
      .column('query_period', { usage: 'group' })
      .column(`${STATISTICS_PREFIX}.document_date`, { usage: 'group', func: 'year' })
      .column(`${STATISTICS_PREFIX}.document_date`, { usage: 'group', func: 'month' });
    addValueColumns(builder);
    builder.sort(`${STATISTICS_PREFIX}.document_date`, 'ASC');
    addPeriodColumns(builder);
    addPeriodFilters(builder, primary, reference);
    addCustomerFilter(builder, customerNumber, subNumber);

    const result = (await repo.load({ query: builder.build() as Query<object> })) as
      | LoadResult
      | null
      | undefined;

    const series = this.buildMonthlySeries(
      (result?.data ?? []).filter((r) => periodOf(r) === 'current')
    );
    const current = sumTotals(
      (result?.data ?? []).filter((r) => periodOf(r) === 'current')
    );
    const previous = sumTotals(
      (result?.data ?? []).filter((r) => periodOf(r) === 'previous')
    );
    return { series, current, previous };
  }

  /** Aggregates raw period rows into the `{ netto, cost, db, dbPercent }`
   *  monthly series (keyed `YYYY-MM`, sorted ascending). */
  private buildMonthlySeries(rows: ApiRow[]): CustomerStatisticsSeriesRow[] {
    const monthMap = new Map<string, { netto: number; cost: number; db: number }>();
    for (const row of rows) {
      const bucket = row[STATISTICS_PREFIX] as ApiRow | undefined;
      const year = bucket?.['document_date:year'];
      const month = bucket?.['document_date:month'];
      if (year == null || month == null) continue;
      const key = `${year}-${String(month).padStart(2, '0')}`;
      const netto = val(row, 'net');
      const cost = val(row, 'cost');
      const db = val(row, 'contribution_margin');
      const existing = monthMap.get(key);
      if (existing) {
        existing.netto += netto;
        existing.cost += cost;
        existing.db += db;
      } else {
        monthMap.set(key, { netto, cost, db });
      }
    }
    return [...monthMap.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([field, v]) => ({
        field,
        netto: v.netto,
        cost: v.cost,
        db: v.db,
        dbPercent: v.netto !== 0 ? (v.db / v.netto) * 100 : 0,
      }));
  }

  async loadTotals(
    customerNumber: number | string,
    subNumber: number | string,
    from: string,
    to: string,
    documentType = 'INVOICE',
    statisticType = 'TOTAL'
  ): Promise<CustomerStatisticsTotals> {
    const repo = new StatisticsRepo(this.dataAccess, documentType, statisticType);
    const builder = new QueryBuilder<ApiRow>().start(0).end(1);
    addValueColumns(builder);
    addDateFilter(builder, from, to);
    addCustomerFilter(builder, customerNumber, subNumber);

    const result = (await repo.load({ query: builder.build() as Query<object> })) as
      | LoadResult
      | null
      | undefined;
    const row = result?.data?.[0] as ApiRow | undefined;
    if (!row) return { netto: 0, cost: 0, db: 0, dbPercent: 0 };
    const netto = val(row, 'net');
    const cost = val(row, 'cost');
    const db = val(row, 'contribution_margin');
    const dbPercent = netto !== 0 ? (db / netto) * 100 : 0;
    return { netto, cost, db, dbPercent };
  }

  async loadMonthlySeries(
    customerNumber: number | string,
    subNumber: number | string,
    from: string,
    to: string,
    documentType = 'INVOICE',
    statisticType = 'TOTAL'
  ): Promise<CustomerStatisticsSeriesRow[]> {
    const repo = new StatisticsRepo(this.dataAccess, documentType, statisticType);
    const builder = new QueryBuilder<ApiRow>()
      .start(0)
      .end(10000)
      .column(`${STATISTICS_PREFIX}.document_date`, { usage: 'group', func: 'year' })
      .column(`${STATISTICS_PREFIX}.document_date`, { usage: 'group', func: 'month' });
    addValueColumns(builder);
    builder.sort(`${STATISTICS_PREFIX}.document_date`, 'ASC');
    addDateFilter(builder, from, to);
    addCustomerFilter(builder, customerNumber, subNumber);

    const result = (await repo.load({ query: builder.build() as Query<object> })) as
      | LoadResult
      | null
      | undefined;

    const monthMap = new Map<string, { netto: number; cost: number; db: number }>();
    for (const row of result?.data ?? []) {
      const bucket = row[STATISTICS_PREFIX] as ApiRow | undefined;
      const ts = bucket?.['document_date'];
      if (!ts) continue;
      const date = new Date(ts as string);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const netto = val(row, 'net');
      const cost = val(row, 'cost');
      const db = val(row, 'contribution_margin');
      const existing = monthMap.get(key);
      if (existing) {
        existing.netto += netto;
        existing.cost += cost;
        existing.db += db;
      } else {
        monthMap.set(key, { netto, cost, db });
      }
    }

    return [...monthMap.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([field, v]) => ({
        field,
        netto: v.netto,
        cost: v.cost,
        db: v.db,
        dbPercent: v.netto !== 0 ? (v.db / v.netto) * 100 : 0,
      }));
  }
}
